import { styled } from "goober";
import { useEffect, useRef, useState } from "react";
import { PDF_VIEWER_ROUTE } from "../../../shared/reservedRoutes";
import { baseMediaType } from "../../../shared/attachmentPolicy";
import { useSessionTenantEdgeOrigin } from "../../hooks/useSessionTenantOrigin";
import { pdfPreviewOrigin } from "../../lib/pdfPreviewOrigin";
import { Icon } from "../Icon";
import { Text } from "../Text";
import { DownloadButton } from "./DownloadButton";
import type { FileRenderControl } from "./types";
import { useFileActions } from "./useFileActions";

const Container = styled("div")`
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
  width: 100%;
  height: 100%;
`;

const Frame = styled("iframe")`
  flex: 1;
  min-height: 480px;
  width: 100%;
  border-radius: 12px;
  border: 0.5px solid var(--border-alpha);
  background: var(--surface-primary);
`;

type Props = {
  url?: string;
  fileName?: string;
  onControls?: (controls: FileRenderControl[]) => void;
};

export function PdfViewer({ blob, title }: { blob: Blob; title: string }) {
  const edgeOrigin = pdfPreviewOrigin(useSessionTenantEdgeOrigin());
  const sentRef = useRef(false);

  if (!edgeOrigin) {
    return <Text variant="text-small">PDF preview unavailable</Text>;
  }

  return (
    <Frame
      src={`${edgeOrigin}${PDF_VIEWER_ROUTE}`}
      title={title}
      onLoad={(event) => {
        if (sentRef.current) return;
        sentRef.current = true;
        event.currentTarget.contentWindow?.postMessage(blob, edgeOrigin);
      }}
    />
  );
}

export function Pdf({ url, fileName, onControls }: Props) {
  const [blob, setBlob] = useState<Blob>();

  useEffect(() => {
    if (!url) return;
    const controller = new AbortController();
    void fetch(url, { credentials: "include", signal: controller.signal })
      .then((response) => (response.ok ? response.blob() : undefined))
      .then((value) => {
        if (
          !controller.signal.aborted &&
          value &&
          baseMediaType(value.type) === "application/pdf"
        ) {
          setBlob(value.slice(0, value.size, "application/pdf"));
        }
      })
      .catch(() => {});
    return () => controller.abort();
  }, [url]);

  const downloadName = fileName ?? "output.pdf";
  const { download, downloading } = useFileActions({
    url,
    fileName: downloadName,
    onControls,
  });

  if (!blob) return null;

  return (
    <Container>
      <PdfViewer blob={blob} title={downloadName} />
      {!onControls && (
        <div style={{ marginLeft: "auto" }}>
          <DownloadButton onPress={download} disabled={!url || downloading}>
            <Icon name="arrow-down" size={12} />
            <Text variant="text-small-md">
              {downloading ? "Downloading…" : "Download"}
            </Text>
          </DownloadButton>
        </div>
      )}
    </Container>
  );
}
